<script>
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { ddStore } from "@common/ddStore";

let vmsCustomAttributeGroupEditConfig = {
    cId: "vmsCustomAttributeGroupEdit",
    cType: "vmsCustomAttributeGroupEdit",
    cTitle: "Change Attribute Group",
    cTemplate: `<div :id="wrapperID" class="vmsCustomAttributeGroupEdit-wrapper" :class="{'is-from-popup': isFromPopup}">
                    <div v-if="attrGroupId">
                        <div class="vmsCustomAttributeGroupEdit-title">Edit Group</div>
                        <div class="vmsCustomAttributeGroupEdit-subTitle">
                            <span>Please change name of current group</span>
                        </div>
                        <div class="vmsCustomAttributeGroupEdit-fieldsWrapper">
                            <control name="CUSTOM_ATTRIBUTE_GROUP_EDIT_NAME"></control>
                            <div v-if="withVisibility()" class="vmsCustomAttributeGroupEdit-visibilityWrapper">
                                <span>Visibility:</span>
                                <control
                                    class="vmsCustomAttributeGroupEdit-visibility"
                                    name="CUSTOM_ATTRIBUTE_GROUP_EDIT_VISIBILITY_RADIO"
                                    :items="visibilityModes"
                                    @update:value="changeVisibilityMode"
                                ></control>
                            </div>
                            <div v-if="isVisibleApplyChangesCtx">
                                <control name="CUSTOM_ATTRIBUTE_GROUP_EDIT_APPLY_CTX_CHECKBOX" :disabled="getVisibilityMode() === 1"></control>
                            </div>
                            <div v-if="withPermissions()" class="vmsCustomAttributeGroupEdit-permissionsWrapper">
                                <a class="vmsCustomAttributeGroupEdit-permissions" @click="openPermissions">Set Permissions and Sharing</a>
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div class="vmsCustomAttributeGroupEdit-title">Add New Group</div>
                        <div class="vmsCustomAttributeGroupEdit-subTitle">
                            <span>Please enter name of the new group</span>
                        </div>
                        <div class="vmsCustomAttributeGroupEdit-fieldsWrapper">
                            <control name="CUSTOM_ATTRIBUTE_GROUP_EDIT_NAME"></control>
                        </div>
                    </div>
                    <div class="vmsCustomAttributeGroupEdit-buttons">
                        <div class="vmsCustomAttributeGroupEdit-button"><control name="CUSTOM_ATTRIBUTE_MANAGER_GROUP_EDIT_OK_BUTTON"></control></div>
                        <div class="vmsCustomAttributeGroupEdit-button"><control name="CUSTOM_ATTRIBUTE_MANAGER_GROUP_EDIT_CANCEL_BUTTON"></control></div>
                    </div>
               </div>
               `,
    controls: [
        {
            name: 'CUSTOM_ATTRIBUTE_GROUP_EDIT_NAME',
            type: 'string',
            hideMessage: true,
            display: true,
            fields: { text: 'text', value: 'value' },
            htmlAttributes: { maxlength: 180 },
            ref: 'vmsCustomAttributeGroupEditName'
        },
        {
            name: 'CUSTOM_ATTRIBUTE_GROUP_EDIT_VISIBILITY_RADIO',
            type: 'radio_button_list',
            hideMessage: true,
            fields: { text: 'text', value: 'value' },
        },
        {
            name: 'CUSTOM_ATTRIBUTE_GROUP_EDIT_APPLY_CTX_CHECKBOX',
            type: 'checkbox',
            label: 'DD.CUSTOM_ATTRIBUTE.CUSTOMATTRIBUTEEDITOR_GROUP_CHANGE_APPLY_CTX'
        },
        // bottom buttons
        {
            name: 'CUSTOM_ATTRIBUTE_MANAGER_GROUP_EDIT_OK_BUTTON',
            type: 'button',
            label: 'OK',
            onclick: 'save',
        },
        {
            name: 'CUSTOM_ATTRIBUTE_MANAGER_GROUP_EDIT_CANCEL_BUTTON',
            type: 'button',
            label: 'Cancel',
            onclick: 'close',
        },
    ]
};

let vmsCustomAttributeGroupEditCmp = {
    name: 'vmsCustomAttributeGroupEdit',
    preInitControls: true,
    props: {
        isFromPopup: {
            default: false
        },
        perspectiveId: {
            type: Number,
            default: null
        },
        attrGroupId: {
            type: Number,
            default: null
        },
        saveCallback: {
            type: Function,
            default: null
        },
    },
    data() {
        return {
            currentVisibilityMode: -1,
            visibilityModes: [],
            isVisibleApplyChangesCtx: false,
            clientPermissions: []
        };
    },
    computed: {
        wrapperID() {
            return this.cId + 'Wrapper';
        },
    },
    components: {
        labelWrapper: defineAsyncComponent(() => import('@common/control/labelWrapper'))
    },
    methods: {
        withVisibility() {
            return this.currentVisibilityMode !== -1 && this.visibilityModes.length > 0;
        },
        withPermissions() {
            return this.clientPermissions.length > 0;
        },
        getVisibilityMode() {
            if (typeof this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_VISIBILITY_RADIO'] !== 'undefined') {
                return this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_VISIBILITY_RADIO'].value;
            }
            return null;
        },
        changeVisibilityMode() {
            if (
                this.isVisibleApplyChangesCtx
                && typeof this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_APPLY_CTX_CHECKBOX'] !== 'undefined'
                && this.getVisibilityMode() === 1
            ) {
                this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_APPLY_CTX_CHECKBOX'].value = 0;
            }
        },
        async onLoad() {
            this.$emit('update:vmsCustomAttributeGroupEditLoaded', true);
            if (typeof this.perspectiveId !== 'number' || !this.perspectiveId) {
                this.perspectiveId = null;
            }
            if (typeof this.attrGroupId !== 'number' || !this.attrGroupId) {
                this.attrGroupId = null;
            }
            if (this.attrGroupId) {
                library.showLoadingIndicator(this.wrapperID);
                let editorParams = await this.getCustomAttributeGroupEditParams();
                if (typeof editorParams === 'object') {
                    if (typeof editorParams.isVisibleApplyChangesCtx === 'boolean') {
                        this.isVisibleApplyChangesCtx = editorParams.isVisibleApplyChangesCtx;
                    }
                    if (typeof editorParams.accessModes === 'object') {
                        let tmpVisibilityModes = [];
                        editorParams.accessModes.forEach((item) => {
                            tmpVisibilityModes.push({
                                text: item.modeName,
                                value: item.modeID
                            });
                        });
                        this.visibilityModes = library.cloneObj(tmpVisibilityModes);
                    }
                }
                let groupInfo = await this.getCustomAttributeGroupInfo(this.attrGroupId);
                if (typeof groupInfo === 'object') {
                    this.currentVisibilityMode = groupInfo['ACCESS_MODE_SELECTED'];
                    if (typeof this.refs['vmsCustomAttributeGroupEditName'] !== 'undefined') {
                        this.refs['vmsCustomAttributeGroupEditName'].value = groupInfo['CUSTOM_ATTRIBUTE_GROUP_NAME'];
                    }
                    if (typeof this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_VISIBILITY_RADIO'] !== 'undefined') {
                        this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_VISIBILITY_RADIO'].value = groupInfo['ACCESS_MODE_SELECTED'];
                    }
                    if (typeof groupInfo['PERMISSIONS'] === 'object') {
                        this.clientPermissions = library.cloneObj(groupInfo['PERMISSIONS']);
                    }
                }
                library.hideLoadingIndicator(this.wrapperID);
            }
        },
        async openPermissions() {
            let clientPermissions = library.cloneObj(this.clientPermissions);

            let changeCallback = async (changedPermissions) => {
                if (typeof changedPermissions === 'object') {
                    this.clientPermissions = library.cloneObj(changedPermissions);
                }
            };

            actionStore.openChangePermissions(clientPermissions, changeCallback);
        },
        async save() {
            let title = await ddStore.getAlias('COMMON', 'ERRORMESSAGE_TITLE');
            let message = null;
            let attrGroupId = this.attrGroupId;
            let groupName = this.refs['vmsCustomAttributeGroupEditName']?.value.trim();
            if (!groupName) {
                let fieldName = await ddStore.getAlias('COMMON', 'CUSTOMATTRIBUTEMANAGER_GROUP_NAME');
                message = await ddStore.getAlias('CUSTOM_ATTRIBUTE', 'CUSTOMATTRIBUTEMANAGER_ERROR_EMPTY', fieldName);
                library.showDialog(message, false, false, {
                    title: title, name: 'vmsCustomAttributeGroupEditWarning', preventCloseOnBackdropClick: true, showAsAlert: true
                });
                return;
            }
            if (attrGroupId) {
                let visibilityMode = null;
                let isApplyChangesCtx = null;
                if (this.withVisibility() && typeof this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_VISIBILITY_RADIO'] !== 'undefined') {
                    visibilityMode = this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_VISIBILITY_RADIO'].value;
                }
                if (this.isVisibleApplyChangesCtx && typeof this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_APPLY_CTX_CHECKBOX'] !== 'undefined') {
                    isApplyChangesCtx = this.controls['CUSTOM_ATTRIBUTE_GROUP_EDIT_APPLY_CTX_CHECKBOX'].value;
                }
                library.showLoadingIndicator(this.wrapperID);
                this.sDs.post('ec_updateCustomAttributeGroupInfo', attrGroupId, groupName, visibilityMode, isApplyChangesCtx).then((response) => {
                    library.hideLoadingIndicator(this.wrapperID);
                    if (response && response.status === 'success') {
                        if (typeof this.saveCallback === 'function') {
                            this.saveCallback();
                        }
                        this.close();
                    } else if (typeof response.error === 'object' && response.error.length) {
                        library.showDialog(response.error[0], false, false, {
                            title: title, name: 'vmsCustomAttributeGroupEditWarning', preventCloseOnBackdropClick: true, showAsAlert: true
                        });
                    }
                });
            } else {
                library.showLoadingIndicator(this.wrapperID);
                this.sDs.post('ec_createCustomAttributeGroup', this.perspectiveId, groupName).then((response) => {
                    library.hideLoadingIndicator(this.wrapperID);
                    if (response && response.status === 'success') {
                        if (typeof this.saveCallback === 'function') {
                            this.saveCallback();
                        }
                        this.close();
                    } else if (typeof response.error === 'object' && response.error.length) {
                        library.showDialog(response.error[0], false, false, {
                            title: title, name: 'vmsCustomAttributeGroupEditWarning', preventCloseOnBackdropClick: true, showAsAlert: true
                        });
                    }
                });
            }
        },
        close() {
            if (/openvmsCustomAttributeGroupEdit/g.test(window.name)) {
                window.close();
            } else if (
                this.$parent
                && this.$parent.$parent
                && this.$parent.$parent.$parent
                && typeof this.$parent.$parent.$parent.componentDialogs === 'object'
                && this.$parent.$parent.$parent.componentDialogs.length
            ) {
                this.$parent.$parent.$parent.componentDialogs.find((component) => component.name === 'openvmsCustomAttributeGroupEdit')?.cancelButton?.click();
            }
        },
        // sDs
        async getCustomAttributeGroupEditParams() {
            let response = await this.sDs.post('ec_getCustomAttributeGroupEditParams');
            return response && response.status === 'success' && typeof response.data !== 'undefined' ? response.data : null;
        },
        async getCustomAttributeGroupInfo(attrGroupId) {
            let response = await this.sDs.post('ec_getCustomAttributeGroupInfo', attrGroupId);
            return response && response.status === 'success' && typeof response.data !== 'undefined' ? response.data : null;
        },
    }
};

componentStore.initComponent(vmsCustomAttributeGroupEditCmp, vmsCustomAttributeGroupEditConfig);
export { vmsCustomAttributeGroupEditConfig, vmsCustomAttributeGroupEditCmp };
export default vmsCustomAttributeGroupEditCmp;
</script>

<style>
#dialog_container_openvmsCustomAttributeGroupEdit, #dialog_container_openvmsCustomAttributeGroupEdit > div {
    min-height: 200px;
}
</style>
<style scoped>
:deep(.vmsCustomAttributeGroupEdit-title) {
    font-size: 24px;
    font-weight: 600;
    text-align: center;
    color: #41362f;
}
:deep(.vmsCustomAttributeGroupEdit-subTitle) {
    margin-top: 20px;
    text-align: center;
}
:deep(.vmsCustomAttributeGroupEdit-Wrapper) {
    display: flex;
}
:deep(.vmsCustomAttributeGroupEdit-mainWrapper) {
    margin-top: 20px;
    width: 100%;
    display: inline-block;
}
:deep(.vmsCustomAttributeGroupEdit-wrapper) {
    width: 100%;
    min-height: 200px;
    background-color: #fff;
    padding: 20px;
}
:deep(.vmsCustomAttributeGroupEdit-fieldsWrapper) {
    margin-top: 20px;
}
:deep(.vmsCustomAttributeGroupEdit-visibilityWrapper) {
    margin-top: 20px;
}
:deep(.vmsCustomAttributeGroupEdit-visibility) {
    top: -10px;
    position: relative;
}
:deep(.vmsCustomAttributeGroupEdit-permissionsWrapper) {
    margin-top: 10px;
}
:deep(.vmsCustomAttributeGroupEdit-permissions) {
    text-decoration: none;
    cursor: pointer;
}
:deep(.is-from-popup) {
    width: 100%;
    height: 100%;
}
:deep(.vmsCustomAttributeGroupEdit-buttons) {
    text-align: center;
    margin-top: 20px;
    clear: both;
}
:deep(.vmsCustomAttributeGroupEdit-button) {
    display: inline-block;
    margin-left: 20px;
    width: 90px;
}
</style>
